const RF_ATLAS_DISTILLERIES = window.RF_ATLAS_DISTILLERIES || [];

const RF_ATLAS_REGIONS = [
  { name: 'Speyside', x: 68, y: 60, originX: 62.4, originY: 54.6, zoom: 3.2 },
  { name: 'Highlands', x: 42, y: 61, originX: 45.4, originY: 59.9, zoom: 2.1 },
  { name: 'Islay', x: 20, y: 81, originX: 30, originY: 81, zoom: 4.2 },
  { name: 'Islands', x: 24, y: 48, originX: 32.2, originY: 50, zoom: 1.65 },
  { name: 'Lowlands', x: 62, y: 84, originX: 54.2, originY: 81.3, zoom: 2.5 },
  { name: 'Campbeltown', x: 35, y: 91, originX: 36.6, originY: 85.5, zoom: 4.6 },
];

class RfLibraryExplorer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.classList.add('rf-library--enhanced');
    this.releases = [...this.querySelectorAll('[data-release]')];
    this.viewButtons = [...this.querySelectorAll('[data-library-view]')];
    this.atlasEnabled = this.dataset.atlasEnabled === 'true';
    this.atlasPanel = this.querySelector('[data-panel="atlas"]');
    this.atlas = this.querySelector('.rf-library__atlas');
    this.mapViewport = this.querySelector('[data-map-viewport]');
    this.mapCanvas = this.querySelector('[data-map-canvas]');
    this.mapZoomIn = this.querySelector('[data-map-zoom-in]');
    this.mapZoomOut = this.querySelector('[data-map-zoom-out]');
    this.mapHome = this.querySelector('[data-map-home]');
    this.mapZoomStatus = this.querySelector('[data-map-zoom-status]');
    this.mapState = { zoom: 1, panX: 0, panY: 0 };
    this.releasePanel = this.querySelector('[data-panel="releases"]');
    this.releaseList = this.querySelector('[data-release-list]');
    this.liveRegion = this.querySelector('[data-library-live]');
    this.filterForm = this.querySelector('[data-filter-form]');
    this.filterDrawer = this.querySelector('[data-filter-drawer]');
    this.filterDrawerOpen = this.querySelector('[data-filter-drawer-open]');
    this.filterDrawerClose = this.querySelector('[data-filter-drawer-close]');
    this.filterDrawerScrim = this.querySelector('[data-filter-drawer-scrim]');
    this.placeTitle = this.querySelector('[data-place-title]');
    this.placeCount = this.querySelector('[data-place-count]');
    this.placeCoordinate = this.querySelector('[data-place-coordinate]');
    this.placeCopy = this.querySelector('[data-place-copy]');
    this.placeList = this.querySelector('[data-place-list]');
    this.placeFacts = this.querySelector('[data-place-facts]');
    this.releaseListHeading = this.querySelector('[data-release-list-heading]');
    this.placeAction = this.querySelector('[data-place-action]');
    this.distilleryLink = this.querySelector('[data-distillery-link]');
    this.provisionalInputs = [...this.querySelectorAll('[data-provisional-filter]')];
    this.applied = this.querySelector('[data-applied]');
    this.appliedList = this.querySelector('[data-applied-list]');
    this.nativeChipCount = this.querySelectorAll('[data-native-chip]').length;
    this.filterBadge = this.querySelector('[data-filter-badge]');
    this.clearRow = this.querySelector('[data-clear-row]');
    this.searchInput = this.querySelector('[data-library-search]');
    this.searchForm = this.querySelector('[data-library-search-form]');
    this.queryField = this.querySelector('[data-library-query-field]');
    this.provisionalEmpty = this.querySelector('[data-provisional-empty]');
    this.emptyNotify = this.querySelector('[data-empty-notify]');
    this.emptyRegion = this.querySelector('[data-empty-region]');
    this.regionChips = [...this.querySelectorAll('[data-region-chip]')];
    this.selectedPlace = null;
    this.initialPlace = {
      title: this.placeTitle?.textContent || '',
      count: this.placeCount?.textContent || '',
      copy: this.placeCopy?.textContent || '',
    };

    this.#buildAtlas();
    this.#bindViews();
    this.#bindForm();
    this.#bindFilterDrawer();
    this.#bindMapControls();
    this.#bindRegionChips();
    this.#prioritisePurchasable();
    this.#restoreProvisionalFilters();
    this.#applyProvisionalFilters(false);

    const initialParams = new URL(window.location.href).searchParams;
    const requestedView = initialParams.get('rf_view') || initialParams.get('view');
    const availableViews = this.atlasEnabled ? ['atlas', 'ledger', 'grid'] : ['ledger', 'grid'];
    this.#setView(availableViews.includes(requestedView) ? requestedView : 'grid', false);
    this.#renderOffMap();
    this.#restoreSelectedPlace();
    this.handlePopState = () => {
      const params = new URL(window.location.href).searchParams;
      const view = params.get('rf_view') || params.get('view');
      this.#setView(availableViews.includes(view) ? view : 'grid', false);
      this.#restoreSelectedPlace();
    };
    window.addEventListener('popstate', this.handlePopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('keydown', this.handleFilterDrawerKeydown);
    this.filterDrawerMedia?.removeEventListener('change', this.handleFilterDrawerMediaChange);
    document.documentElement.classList.remove('rf-library-filter-open');
  }

  #normalise(value) {
    return (value || '').trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ');
  }

  #slugify(value) {
    return (value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  #matchingReleases(kind, name) {
    const expected = this.#normalise(name);
    return this.releases.filter((release) => this.#normalise(release.dataset[kind]) === expected);
  }

  #renderOffMap() {
    // Releases whose distillery has no atlas coordinate simply never appeared
    // on the map: Cooley is in Ireland, Bellevue is in Guadeloupe, and a handful
    // of Scottish distilleries are still awaiting verified coordinates. Left
    // alone they are unreachable from place-led browsing, so list them.
    const host = this.querySelector('[data-atlas-offmap]');
    if (!host) return;
    const mapped = new Set(
      (window.RF_ATLAS_DISTILLERIES || []).map((p) => this.#normalise(p.name))
    );
    const groups = new Map();
    for (const release of this.releases) {
      const name = (release.dataset.distillery || '').trim();
      if (!name || mapped.has(this.#normalise(name))) continue;
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(release);
    }
    if (!groups.size) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    const list = document.createElement('ul');
    list.className = 'rf-library__offmap-list';
    for (const [name, items] of [...groups].sort((a, b) => a[0].localeCompare(b[0]))) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rf-library__offmap-item';
      button.dataset.atlasPlace = name;
      button.innerHTML =
        `<span>${name}</span><span class="rf-library__offmap-count">${items.length}</span>`;
      button.addEventListener('click', () => this.#selectPlace('distillery', name, button));
      li.append(button);
      list.append(li);
    }
    host.querySelector('[data-atlas-offmap-list]')?.replaceWith(list);
    list.setAttribute('data-atlas-offmap-list', '');
  }

  #restoreSelectedPlace() {
    const params = new URL(window.location.href).searchParams;
    const requestedPlace = params.get('place');
    if (!requestedPlace) return;
    const requestedKind = params.get('place_kind');
    const button = [...this.querySelectorAll('[data-atlas-place]')].find((candidate) => {
      const kindMatches = !requestedKind || candidate.dataset.atlasKind === requestedKind;
      return kindMatches && this.#slugify(candidate.dataset.atlasPlace) === requestedPlace;
    });
    if (button) this.#selectPlace(button.dataset.atlasKind, button.dataset.atlasPlace, button, false);
  }

  #buildAtlas() {
    const markerLayer = this.querySelector('[data-atlas-markers]');
    const regionLayer = this.querySelector('[data-atlas-regions]');
    const regionIndex = this.querySelector('[data-region-index]');
    if (!markerLayer || !regionLayer) return;

    const placed = new Map();
    for (const point of RF_ATLAS_DISTILLERIES) {
      const matches = this.#matchingReleases('distillery', point.name);
      if (!matches.length) continue;
      const marker = document.createElement('button');
      marker.type = 'button';
      marker.className = 'rf-library__marker';
      // Some brands share a physical site (Inchmurrin and Old Rhosdhu are both
      // Loch Lomond), so their points coincide exactly and one would be
      // unclickable. Fan co-located markers apart visually; the underlying
      // coordinates are untouched.
      const key = `${point.x},${point.y}`;
      const seen = placed.get(key) || 0;
      placed.set(key, seen + 1);
      const angle = seen * 2.4;
      const spread = seen === 0 ? 0 : 0.55;
      marker.style.insetInlineStart = `${point.x + Math.cos(angle) * spread}%`;
      marker.style.insetBlockStart = `${point.y + Math.sin(angle) * spread}%`;
      marker.dataset.atlasPlace = point.name;
      marker.dataset.atlasKind = 'distillery';
      marker.setAttribute('aria-pressed', 'false');
      marker.setAttribute('aria-label', `${point.name}: ${matches.length} ${matches.length === 1 ? 'release' : 'releases'}`);
      marker.title = `${point.name} · ${matches.length}`;
      marker.addEventListener('click', () => this.#selectPlace('distillery', point.name, marker));
      markerLayer.append(marker);
    }

    for (const region of RF_ATLAS_REGIONS) {
      const matches = this.#matchingReleases('region', region.name);
      if (!matches.length) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rf-library__region';
      button.style.insetInlineStart = `${region.x}%`;
      button.style.insetBlockStart = `${region.y}%`;
      button.dataset.atlasPlace = region.name;
      button.dataset.atlasKind = 'region';
      button.setAttribute('aria-pressed', 'false');

      const name = document.createElement('span');
      name.className = 'rf-library__region-name';
      name.textContent = region.name;
      const count = document.createElement('span');
      count.className = 'rf-library__region-count';
      count.textContent = `${matches.length} ${matches.length === 1 ? 'release' : 'releases'}`;
      button.append(name, count);
      button.addEventListener('click', () => this.#selectPlace('region', region.name, button));
      regionLayer.append(button);

      const undisclosed = matches.filter((release) => !release.dataset.distillery);
      if (undisclosed.length) {
        const originMarker = document.createElement('button');
        originMarker.type = 'button';
        originMarker.className = 'rf-library__origin-marker';
        originMarker.style.insetInlineStart = `${region.originX}%`;
        originMarker.style.insetBlockStart = `${region.originY}%`;
        originMarker.dataset.atlasPlace = region.name;
        originMarker.dataset.atlasKind = 'region';
        originMarker.dataset.atlasUndisclosed = 'true';
        originMarker.dataset.label = 'Distillery undisclosed';
        originMarker.setAttribute('aria-pressed', 'false');
        originMarker.setAttribute('aria-label', `${region.name}: ${undisclosed.length} ${undisclosed.length === 1 ? 'release has' : 'releases have'} an undisclosed distillery. This is a regional marker, not an exact location.`);
        originMarker.addEventListener('click', () => this.#selectPlace('region', region.name, originMarker));
        markerLayer.append(originMarker);
      }

      if (regionIndex) {
        const indexButton = document.createElement('button');
        indexButton.type = 'button';
        indexButton.className = 'rf-library__index-button';
        indexButton.dataset.atlasPlace = region.name;
        indexButton.dataset.atlasKind = 'region';
        indexButton.setAttribute('aria-pressed', 'false');
        const indexName = document.createElement('span');
        indexName.className = 'rf-library__index-name';
        indexName.textContent = region.name;
        const indexCount = document.createElement('span');
        indexCount.className = 'rf-library__index-count';
        indexCount.textContent = String(matches.length);
        indexButton.append(indexName, indexCount);
        indexButton.addEventListener('click', () => this.#selectPlace('region', region.name, indexButton));
        regionIndex.append(indexButton);
      }
    }
  }

  #selectPlace(kind, name, trigger, updateUrl = true) {
    const matches = this.#matchingReleases(kind, name).filter((release) => !release.hidden);
    this.selectedPlace = { kind, name, matches };
    for (const button of this.querySelectorAll('[data-atlas-place]')) {
      const selected = button.dataset.atlasKind === kind && this.#normalise(button.dataset.atlasPlace) === this.#normalise(name);
      button.setAttribute('aria-pressed', String(selected));
      if (selected && kind === 'distillery') {
        button.dataset.inFocus = 'true';
        button.dataset.label = `${name} · ${matches.length}`;
      }
    }

    if (kind === 'region') this.#zoomRegion(name);
    if (kind === 'distillery' && this.mapState.zoom < 2) this.#zoomDistillery(name);
    this.placeTitle.textContent = name;
    this.placeCount.textContent = `${matches.length} ${matches.length === 1 ? 'release' : 'releases'} in the shop`;
    this.placeCopy.hidden = false;
    this.releaseListHeading.hidden = matches.length === 0;
    this.#renderPlaceFacts(kind, name, matches);
    this.placeList.replaceChildren();

    for (const release of matches.slice(0, 5)) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = release.dataset.url;
      link.className = 'rf-library__place-release';
      const media = document.createElement('span');
      media.className = 'rf-library__place-release-media';
      media.setAttribute('aria-hidden', 'true');
      if (release.dataset.image) {
        const image = document.createElement('img');
        image.src = release.dataset.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 56;
        image.height = 72;
        media.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'rf-library__place-release-placeholder';
        media.append(placeholder);
      }
      link.append(media);
      const content = document.createElement('span');
      content.className = 'rf-library__place-release-copy';
      const title = document.createElement('span');
      title.className = 'rf-library__place-release-title';
      title.textContent = release.dataset.title;
      const details = document.createElement('span');
      details.className = 'rf-library__place-release-meta';
      const releaseAge = Number(release.dataset.age || 0);
      const releaseAbv = Number(release.dataset.abv || 0);
      details.textContent = [releaseAge > 0 ? `${releaseAge} yr` : '', releaseAbv > 0 ? `${releaseAbv.toFixed(1)}%` : '', release.dataset.caskDetail || release.dataset.cask].filter(Boolean).join(' · ');
      content.append(title, details);
      link.append(content);
      item.append(link);
      this.placeList.append(item);
    }

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('rf_view', 'atlas');
      url.searchParams.delete('view');
      url.searchParams.set('place', this.#slugify(name));
      url.searchParams.set('place_kind', kind);
      window.history.pushState({}, '', url);
    }

    if (this.distilleryLink) {
      this.distilleryLink.hidden = kind !== 'distillery';
      if (kind === 'distillery') {
        this.distilleryLink.href = `/collections/distillery-${this.#slugify(name)}`;
        this.distilleryLink.textContent = `View ${name}`;
      }
    }

    const nativeFilter = this.#findNativeFilter(kind, name);
    this.placeAction.textContent = nativeFilter ? `Show ${name} releases` : 'Show all releases';
    this.placeAction.hidden = false;
    this.placeAction.onclick = () => {
      if (nativeFilter && this.filterForm) {
        nativeFilter.checked = true;
        for (const field of this.querySelectorAll('[data-view-field]')) field.value = 'grid';
        this.filterForm.requestSubmit();
        return;
      }
      this.#focusMatches(matches);
    };
    this.#announce(`${name} selected. ${matches.length} releases.`);
  }

  #bindMapControls() {
    if (!this.mapViewport || !this.mapCanvas) return;
    this.mapZoomIn?.addEventListener('click', () => this.#zoomFromCentre(this.mapState.zoom * 1.35));
    this.mapZoomOut?.addEventListener('click', () => this.#zoomFromCentre(this.mapState.zoom / 1.35));
    this.mapHome?.addEventListener('click', () => this.#resetMap());
    this.mapViewport.addEventListener('wheel', (event) => this.#handleMapWheel(event), { passive: false });
    this.mapViewport.addEventListener('pointerdown', (event) => this.#startMapDrag(event));
    this.mapViewport.addEventListener('keydown', (event) => this.#handleMapKeydown(event));
  }

  #setMapTransform(zoom, panX, panY, announce = false) {
    if (!this.mapCanvas || !this.mapViewport) return;
    const nextZoom = Math.min(6, Math.max(1, zoom));
    const rect = this.mapViewport.getBoundingClientRect();
    const maxPanX = rect.width * Math.max(0, nextZoom - 1) * .58;
    const maxPanY = rect.height * Math.max(0, nextZoom - 1) * .58;
    const nextPanX = nextZoom === 1 ? 0 : Math.min(maxPanX, Math.max(-maxPanX, panX));
    const nextPanY = nextZoom === 1 ? 0 : Math.min(maxPanY, Math.max(-maxPanY, panY));
    this.mapState = { zoom: nextZoom, panX: nextPanX, panY: nextPanY };
    this.mapCanvas.style.setProperty('--atlas-zoom', String(nextZoom));
    this.mapCanvas.style.setProperty('--atlas-inverse-zoom', String(1 / nextZoom));
    this.mapCanvas.style.setProperty('--atlas-pan-x', `${nextPanX}px`);
    this.mapCanvas.style.setProperty('--atlas-pan-y', `${nextPanY}px`);
    this.atlas.classList.toggle('is-zoomed', nextZoom > 1.02);
    if (this.mapZoomStatus) this.mapZoomStatus.value = `${Math.round(nextZoom * 100)}%`;
    if (announce) this.#announce(`Map zoom ${Math.round(nextZoom * 100)} percent.`);
  }

  #zoomFromCentre(zoom) {
    const ratio = zoom / this.mapState.zoom;
    this.#setMapTransform(zoom, this.mapState.panX * ratio, this.mapState.panY * ratio, true);
  }

  #zoomRegion(name) {
    const region = RF_ATLAS_REGIONS.find((candidate) => candidate.name === name);
    if (!region || !this.mapViewport) return;
    const rect = this.mapViewport.getBoundingClientRect();
    const pointX = (region.originX / 100 - .5) * rect.width;
    const pointY = (region.originY / 100 - .5) * rect.height;
    this.#setMapTransform(region.zoom, -pointX * region.zoom, -pointY * region.zoom);

    for (const marker of this.querySelectorAll('.rf-library__marker')) {
      const belongs = this.#matchingReleases('distillery', marker.dataset.atlasPlace)
        .some((release) => this.#normalise(release.dataset.region) === this.#normalise(name));
      marker.dataset.inFocus = String(belongs);
      marker.dataset.label = marker.dataset.atlasPlace;
    }
    for (const marker of this.querySelectorAll('.rf-library__origin-marker')) {
      marker.dataset.inFocus = String(this.#normalise(marker.dataset.atlasPlace) === this.#normalise(name));
    }
    this.#announce(`${name} enlarged. Drag to move and use the controls or mouse wheel to zoom.`);
  }

  #zoomDistillery(name) {
    const point = RF_ATLAS_DISTILLERIES.find((candidate) => this.#normalise(candidate.name) === this.#normalise(name));
    if (!point || !this.mapViewport) return;
    const zoom = 3.8;
    const rect = this.mapViewport.getBoundingClientRect();
    const pointX = (point.x / 100 - .5) * rect.width;
    const pointY = (point.y / 100 - .5) * rect.height;
    this.#setMapTransform(zoom, -pointX * zoom, -pointY * zoom);
  }

  #handleMapWheel(event) {
    // Plain wheel/trackpad scrolling belongs to the page. Without this guard the
    // map swallows the scroll whenever the cursor crosses it, and the visitor
    // gets stuck part-way down a long collection page. Zoom is opt-in via a
    // modifier, or by focusing the map and using + / - / arrows.
    if (!event.ctrlKey && !event.metaKey) {
      this.#flashZoomHint();
      return;
    }
    event.preventDefault();
    const rect = this.mapViewport.getBoundingClientRect();
    const cursorX = event.clientX - rect.left - rect.width / 2;
    const cursorY = event.clientY - rect.top - rect.height / 2;
    const oldZoom = this.mapState.zoom;
    const nextZoom = Math.min(6, Math.max(1, oldZoom * Math.exp(-event.deltaY * .0015)));
    const worldX = (cursorX - this.mapState.panX) / oldZoom;
    const worldY = (cursorY - this.mapState.panY) / oldZoom;
    this.#setMapTransform(nextZoom, cursorX - worldX * nextZoom, cursorY - worldY * nextZoom);
  }

  #flashZoomHint() {
    if (!this.mapViewport || this.mapViewport.dataset.zoomHint === 'on') return;
    this.mapViewport.dataset.zoomHint = 'on';
    clearTimeout(this.zoomHintTimer);
    this.zoomHintTimer = setTimeout(() => {
      delete this.mapViewport.dataset.zoomHint;
    }, 1400);
  }

  #startMapDrag(event) {
    if (event.button !== 0 || event.target.closest('button, a')) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = this.mapState.panX;
    const originY = this.mapState.panY;
    this.mapViewport.setPointerCapture(event.pointerId);
    this.mapViewport.classList.add('is-dragging');
    const move = (moveEvent) => {
      this.#setMapTransform(this.mapState.zoom, originX + moveEvent.clientX - startX, originY + moveEvent.clientY - startY);
    };
    const end = () => {
      this.mapViewport.classList.remove('is-dragging');
      this.mapViewport.removeEventListener('pointermove', move);
      this.mapViewport.removeEventListener('pointerup', end);
      this.mapViewport.removeEventListener('pointercancel', end);
    };
    this.mapViewport.addEventListener('pointermove', move);
    this.mapViewport.addEventListener('pointerup', end);
    this.mapViewport.addEventListener('pointercancel', end);
  }

  #handleMapKeydown(event) {
    const step = event.shiftKey ? 80 : 36;
    if (event.key === '+' || event.key === '=') this.#zoomFromCentre(this.mapState.zoom * 1.3);
    else if (event.key === '-') this.#zoomFromCentre(this.mapState.zoom / 1.3);
    else if (event.key === 'Home' || event.key === '0') this.#resetMap();
    else if (event.key === 'ArrowLeft') this.#setMapTransform(this.mapState.zoom, this.mapState.panX + step, this.mapState.panY);
    else if (event.key === 'ArrowRight') this.#setMapTransform(this.mapState.zoom, this.mapState.panX - step, this.mapState.panY);
    else if (event.key === 'ArrowUp') this.#setMapTransform(this.mapState.zoom, this.mapState.panX, this.mapState.panY + step);
    else if (event.key === 'ArrowDown') this.#setMapTransform(this.mapState.zoom, this.mapState.panX, this.mapState.panY - step);
    else return;
    event.preventDefault();
  }

  #resetMap() {
    this.#setMapTransform(1, 0, 0);
    this.selectedPlace = null;
    for (const marker of this.querySelectorAll('[data-atlas-place]')) {
      marker.setAttribute('aria-pressed', 'false');
      delete marker.dataset.inFocus;
      if (marker.classList.contains('rf-library__marker')) marker.dataset.label = marker.dataset.atlasPlace;
    }
    if (this.placeTitle) this.placeTitle.textContent = this.initialPlace.title;
    if (this.placeCount) {
      const visible = this.releases.filter((release) => !release.hidden).length;
      this.placeCount.textContent = `${visible} ${visible === 1 ? 'release' : 'releases'} mapped`;
    }
    if (this.placeCopy) {
      this.placeCopy.textContent = this.initialPlace.copy.trim();
      this.placeCopy.hidden = false;
    }
    if (this.placeCoordinate) {
      this.placeCoordinate.textContent = '';
      this.placeCoordinate.hidden = true;
    }
    if (this.placeFacts) {
      this.placeFacts.replaceChildren();
      this.placeFacts.hidden = true;
    }
    if (this.releaseListHeading) this.releaseListHeading.hidden = true;
    this.placeList?.replaceChildren();
    if (this.distilleryLink) this.distilleryLink.hidden = true;
    if (this.placeAction) {
      this.placeAction.hidden = true;
      this.placeAction.onclick = null;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('place');
    url.searchParams.delete('place_kind');
    window.history.replaceState({}, '', url);
    this.#announce('All Scotland view restored.');
  }

  #renderPlaceFacts(kind, name, matches) {
    if (!this.placeFacts) return;
    const ages = matches.map((release) => Number(release.dataset.age)).filter((value) => value > 0);
    const strengths = matches.map((release) => Number(release.dataset.abv)).filter((value) => value > 0);
    const outturns = matches.map((release) => Number(release.dataset.outturn)).filter((value) => value > 0);
    const casks = [...new Set(matches.map((release) => release.dataset.cask).filter(Boolean))];
    const distilleries = new Set(matches.map((release) => release.dataset.distillery).filter(Boolean));
    const regions = [...new Set(matches.map((release) => release.dataset.region).filter(Boolean))];
    const first = matches[0];
    const ageRange = ages.length ? `${Math.min(...ages)}–${Math.max(...ages)} yr` : '';
    const strengthRange = strengths.length ? `${Math.min(...strengths).toFixed(1)}–${Math.max(...strengths).toFixed(1)}%` : '';
    const outturnRange = outturns.length ? `${Math.min(...outturns)}–${Math.max(...outturns)} bottles` : '';
    if (this.placeCopy) {
      const releaseWord = matches.length === 1 ? 'release' : 'releases';
      const details = [];
      if (ages.length) details.push(`aged ${ageRange.replace(' yr', ' years')}`);
      if (strengths.length) details.push(`bottled at ${strengthRange}`);
      if (kind === 'distillery') {
        this.placeCopy.textContent = `Rare Find has ${matches.length} ${name} ${releaseWord}${details.length ? `, ${details.join(' and ')}` : ''}.`;
      } else {
        const distilleryWord = distilleries.size === 1 ? 'disclosed distillery' : 'disclosed distilleries';
        this.placeCopy.textContent = `${matches.length} ${name} ${releaseWord} across ${distilleries.size} ${distilleryWord}${details.length ? `, ${details.join(' and ')}` : ''}.`;
      }
    }
    const facts = [];
    if (kind === 'distillery' && regions.length) facts.push(['Region', regions.join(', ')]);
    if (first?.dataset.distilleryFounded) facts.push(['Founded', first.dataset.distilleryFounded]);
    if (first?.dataset.distilleryLocation) facts.push(['Location', first.dataset.distilleryLocation]);
    if (first?.dataset.distilleryCountry) facts.push(['Country', first.dataset.distilleryCountry]);
    facts.push(['Releases', String(matches.length)]);
    if (kind === 'region') facts.push(['Disclosed distilleries', String(distilleries.size)]);
    if (ageRange) facts.push(['Age span', ageRange]);
    if (strengthRange) facts.push(['Strength', strengthRange]);
    if (outturnRange) facts.push(['Original outturn', outturnRange]);
    if (casks.length) facts.push(['Cask records', String(casks.length)]);

    this.placeFacts.replaceChildren();
    for (const [label, value] of facts) {
      const term = document.createElement('dt');
      term.textContent = label;
      const detail = document.createElement('dd');
      detail.textContent = value;
      this.placeFacts.append(term, detail);
    }
    this.placeFacts.hidden = false;

    const point = kind === 'distillery' ? RF_ATLAS_DISTILLERIES.find((candidate) => this.#normalise(candidate.name) === this.#normalise(name)) : null;
    if (this.placeCoordinate) {
      this.placeCoordinate.hidden = !point;
      this.placeCoordinate.textContent = point ? `${Math.abs(point.latitude).toFixed(4)}° N · ${Math.abs(point.longitude).toFixed(4)}° ${point.longitude < 0 ? 'W' : 'E'}` : '';
    }
  }

  #findNativeFilter(kind, name) {
    const expected = this.#normalise(name);
    return [...this.querySelectorAll('[data-native-filter-value]')].find((input) => {
      return input.dataset.filterKind === kind && this.#normalise(input.dataset.nativeFilterValue) === expected;
    });
  }

  #focusMatches(matches) {
    this.#setView('grid');
    for (const release of this.releases) release.classList.toggle('is-focused', matches.includes(release));
    matches[0]?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  }

  #bindViews() {
    for (const button of this.viewButtons) {
      button.addEventListener('click', () => this.#setView(button.dataset.libraryView));
    }
  }

  #setView(view, updateUrl = true) {
    if (view === 'atlas' && !this.atlasEnabled) view = 'grid';
    const isAtlas = view === 'atlas';
    this.atlasPanel.dataset.active = String(isAtlas);
    this.releasePanel.dataset.active = String(!isAtlas);
    this.atlasPanel.setAttribute('aria-hidden', String(!isAtlas));
    this.releasePanel.setAttribute('aria-hidden', String(isAtlas));
    this.releaseList.classList.toggle('rf-library__release-list--grid', view === 'grid');
    this.releaseList.classList.toggle('rf-library__release-list--ledger', view !== 'grid');

    for (const button of this.viewButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.libraryView === view));
    }
    for (const field of this.querySelectorAll('[data-view-field]')) field.value = view;

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('rf_view', view);
      url.searchParams.delete('view');
      window.history.replaceState({}, '', url);
      this.#announce(`${view[0].toUpperCase()}${view.slice(1)} view selected.`);
    }
  }

  #bindFilterDrawer() {
    if (!this.filterDrawer || !this.filterDrawerOpen || !this.filterDrawerClose || !this.filterDrawerScrim) return;

    // Must match the drawer breakpoint in rf-library-shell.css.
    this.filterDrawerMedia = window.matchMedia('(max-width: 1180px)');
    const closeDrawer = (restoreFocus = true) => {
      if (restoreFocus && this.filterDrawer.contains(document.activeElement)) this.filterDrawerOpen.focus();
      this.filterDrawer.dataset.open = 'false';
      this.filterDrawerOpen.setAttribute('aria-expanded', 'false');
      this.filterDrawerScrim.hidden = true;
      this.filterDrawer.removeAttribute('role');
      this.filterDrawer.removeAttribute('aria-modal');
      document.documentElement.classList.remove('rf-library-filter-open');
      if (this.filterDrawerMedia.matches) this.filterDrawer.setAttribute('aria-hidden', 'true');
      else this.filterDrawer.removeAttribute('aria-hidden');
    };
    const openDrawer = () => {
      if (!this.filterDrawerMedia.matches) return;
      this.filterDrawer.dataset.open = 'true';
      this.filterDrawer.removeAttribute('aria-hidden');
      this.filterDrawer.setAttribute('role', 'dialog');
      this.filterDrawer.setAttribute('aria-modal', 'true');
      this.filterDrawerOpen.setAttribute('aria-expanded', 'true');
      this.filterDrawerScrim.hidden = false;
      document.documentElement.classList.add('rf-library-filter-open');
      this.filterDrawerClose.focus();
    };

    this.filterDrawerOpen.addEventListener('click', openDrawer);
    this.querySelector('[data-filter-drawer-apply]')?.addEventListener('click', () => closeDrawer());
    this.filterDrawerClose.addEventListener('click', () => closeDrawer());
    this.filterDrawerScrim.addEventListener('click', () => closeDrawer());
    this.handleFilterDrawerKeydown = (event) => {
      if (this.filterDrawer.dataset.open !== 'true') return;
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.filterDrawer.querySelectorAll('button, a[href], input:not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    this.handleFilterDrawerMediaChange = () => closeDrawer(false);
    window.addEventListener('keydown', this.handleFilterDrawerKeydown);
    this.filterDrawerMedia.addEventListener('change', this.handleFilterDrawerMediaChange);
    closeDrawer(false);
  }

  #bindForm() {
    const sort = this.querySelector('[data-library-sort]');
    sort?.addEventListener('change', () => this.filterForm?.requestSubmit());
    for (const input of this.provisionalInputs) {
      input.addEventListener('change', () => this.#applyProvisionalFilters());
    }
    // Shopify filters need a round trip; apply them as soon as they change so
    // the panel behaves the same as the instant Library filters.
    for (const input of this.querySelectorAll('[data-native-filter]')) {
      input.addEventListener('change', () => this.filterForm?.requestSubmit());
    }
    this.filterForm?.addEventListener('submit', () => {
      if (!this.queryField) return;
      const query = this.searchInput?.value.trim() || '';
      this.queryField.value = query;
      this.queryField.disabled = !query;
    });
    if (this.searchInput) {
      let timer;
      this.searchInput.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.#applyProvisionalFilters(), 120);
      });
      // Enter keeps the grid filtered in place; the form still posts to /search without JS.
      this.searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#applyProvisionalFilters();
      });
    }
    for (const link of this.querySelectorAll('[data-clear-all]')) {
      link.addEventListener('click', (event) => {
        if (this.nativeChipCount > 0) return;
        event.preventDefault();
        for (const input of this.provisionalInputs) input.checked = false;
        if (this.searchInput) this.searchInput.value = '';
        this.#applyProvisionalFilters();
      });
    }
  }

  // Region chips above the grid on smaller screens drive the same rf_region
  // checkboxes as the filter drawer, so URL, pills and counts stay in step.
  #bindRegionChips() {
    for (const chip of this.regionChips) {
      chip.addEventListener('click', () => {
        const input = this.#regionInput(chip.dataset.regionChip);
        if (!input) return;
        input.checked = !input.checked;
        this.#applyProvisionalFilters();
      });
    }
  }

  #regionInput(value) {
    return this.provisionalInputs.find((input) => input.dataset.filterKind === 'region' && input.value === value);
  }

  // Featured order keeps the merchandiser's sequence, but once anything can
  // be bought, bottles on sale come first and sold-out or unreleased follow.
  #prioritisePurchasable() {
    if (!this.releaseList || this.dataset.sort !== 'manual') return;
    if (Number(this.dataset.purchasableCount || 0) === 0) return;
    const ordered = [
      ...this.releases.filter((release) => release.dataset.purchasable === 'true'),
      ...this.releases.filter((release) => release.dataset.purchasable !== 'true'),
    ];
    if (ordered.every((release, index) => release === this.releases[index])) return;
    for (const release of ordered) this.releaseList.append(release);
    this.releases = ordered;
  }

  #restoreProvisionalFilters() {
    const params = new URL(window.location.href).searchParams;
    for (const input of this.provisionalInputs) {
      const selected = params.getAll(`rf_${input.dataset.filterKind}`);
      input.checked = selected.includes(input.value);
    }
    if (this.searchInput) this.searchInput.value = params.get('rf_q') || '';
    // Groups holding an active choice open, so applied filters are never hidden.
    for (const input of this.querySelectorAll('.rf-library__filter input:checked')) {
      for (let group = input.closest('details'); group; group = group.parentElement?.closest('details')) group.open = true;
    }
  }

  #matchesAge(release, range) {
    const age = Number(release.dataset.age || 0);
    if (range === 'under-10') return age > 0 && age < 10;
    if (range === '10-14') return age >= 10 && age < 15;
    if (range === '15-19') return age >= 15 && age < 20;
    if (range === '20-24') return age >= 20 && age < 25;
    if (range === '25-plus') return age >= 25;
    return true;
  }

  #matchesAbv(release, range) {
    const abv = Number(release.dataset.abv || 0);
    if (range === 'under-46') return abv > 0 && abv < 46;
    if (range === '46-49') return abv >= 46 && abv < 50;
    if (range === '50-54') return abv >= 50 && abv < 55;
    if (range === '55-plus') return abv >= 55;
    return true;
  }

  #matchesPrice(release, range) {
    const price = Number(release.dataset.price || 0);
    if (range === 'under-80') return price > 0 && price < 80;
    if (range === '80-99') return price >= 80 && price < 100;
    if (range === '100-149') return price >= 100 && price < 150;
    if (range === '150-249') return price >= 150 && price < 250;
    if (range === '250-plus') return price >= 250;
    return true;
  }

  #applyProvisionalFilters(updateUrl = true) {
    const selections = new Map();
    for (const input of this.provisionalInputs.filter((candidate) => candidate.checked)) {
      const kind = input.dataset.filterKind;
      if (!selections.has(kind)) selections.set(kind, []);
      selections.get(kind).push(input.value);
    }

    const rawQuery = this.searchInput?.value.trim() || '';
    const query = this.#normalise(rawQuery).trim();

    let visible = 0;
    for (const release of this.releases) {
      let matches = true;
      for (const [kind, values] of selections) {
        if (kind === 'age') {
          if (!values.some((value) => this.#matchesAge(release, value))) matches = false;
        } else if (kind === 'abv') {
          if (!values.some((value) => this.#matchesAbv(release, value))) matches = false;
        } else if (kind === 'price') {
          if (!values.some((value) => this.#matchesPrice(release, value))) matches = false;
        } else {
          // Exact match for every kind, cask included, so counts agree with the panel.
          const releaseValue = this.#normalise(release.dataset[kind]);
          if (!values.some((value) => this.#normalise(value) === releaseValue)) matches = false;
        }
      }
      if (matches && query) {
        const haystack = this.#normalise(`${release.dataset.title} ${release.dataset.distillery} ${release.dataset.region}`);
        if (!query.split(' ').every((term) => haystack.includes(term))) matches = false;
      }
      release.hidden = !matches;
      if (matches) visible += 1;
    }

    for (const count of this.querySelectorAll('[data-visible-count]')) count.textContent = String(visible);
    for (const noun of this.querySelectorAll('[data-count-noun]')) noun.textContent = visible === 1 ? noun.dataset.one : noun.dataset.other;
    if (this.provisionalEmpty) this.provisionalEmpty.hidden = visible > 0;
    const regions = selections.get('region') || [];
    for (const chip of this.regionChips) {
      chip.setAttribute('aria-pressed', String(Boolean(this.#regionInput(chip.dataset.regionChip)?.checked)));
    }
    if (this.emptyNotify) {
      this.emptyNotify.hidden = visible > 0 || regions.length === 0;
      if (this.emptyRegion) this.emptyRegion.textContent = regions.join(' or ');
    }
    this.#renderProvisionalActive(selections, rawQuery);
    this.#refreshAtlasCounts();

    if (updateUrl) {
      const url = new URL(window.location.href);
      for (const kind of ['region', 'distillery', 'cask', 'age', 'abv', 'price', 'q']) url.searchParams.delete(`rf_${kind}`);
      for (const [kind, values] of selections) {
        for (const value of values) url.searchParams.append(`rf_${kind}`, value);
      }
      if (rawQuery) url.searchParams.set('rf_q', rawQuery);
      window.history.replaceState({}, '', url);
      this.#announce(`${visible} releases match the selected filters.`);
    }
  }

  #renderProvisionalActive(selections, rawQuery = '') {
    if (!this.applied || !this.appliedList) return;
    for (const chip of this.appliedList.querySelectorAll('[data-provisional-chip]')) chip.remove();
    const addChip = (label, onRemove) => {
      const item = document.createElement('li');
      item.dataset.provisionalChip = '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rf-library__active-link';
      const text = document.createElement('span');
      text.textContent = label;
      const mark = document.createElement('span');
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = '×';
      button.append(text, mark);
      button.setAttribute('aria-label', `Remove ${label}`);
      button.addEventListener('click', () => {
        onRemove();
        this.#applyProvisionalFilters();
      });
      item.append(button);
      this.appliedList.append(item);
    };
    let provisionalCount = 0;
    if (rawQuery) {
      provisionalCount += 1;
      addChip(`“${rawQuery}”`, () => { if (this.searchInput) this.searchInput.value = ''; });
    }
    // Group names so a chip reads "Age: 10 to 14 years", not a bare "10–14".
    const groupNames = {region: 'Region', distillery: 'Distillery', cask: 'Cask', age: 'Age', abv: 'Strength', price: 'Budget'};
    const summary = [];
    for (const [kind, values] of selections) {
      for (const value of values) {
        const input = this.provisionalInputs.find((candidate) => candidate.dataset.filterKind === kind && candidate.value === value);
        const label = input?.closest('label')?.querySelector('span')?.textContent.trim() || value;
        const full = groupNames[kind] ? `${groupNames[kind]}: ${label}` : label;
        provisionalCount += 1;
        summary.push(full);
        addChip(full, () => { if (input) input.checked = false; });
      }
    }
    if (rawQuery) summary.unshift(`Search: “${rawQuery}”`);
    const summaryEl = this.querySelector('[data-filter-summary]');
    if (summaryEl) {
      summaryEl.textContent = summary.length ? `Showing ${summary.join(' · ')}` : '';
      summaryEl.hidden = summary.length === 0;
    }
    const total = this.nativeChipCount + provisionalCount;
    this.applied.hidden = total === 0;
    if (this.clearRow) this.clearRow.hidden = total === 0;
    if (this.filterBadge) {
      this.filterBadge.textContent = String(total);
      this.filterBadge.hidden = total === 0;
    }
  }

  #refreshAtlasCounts() {
    for (const button of this.querySelectorAll('[data-atlas-place]')) {
      let matches = this.#matchingReleases(button.dataset.atlasKind, button.dataset.atlasPlace).filter((release) => !release.hidden);
      if (button.dataset.atlasUndisclosed === 'true') matches = matches.filter((release) => !release.dataset.distillery);
      button.hidden = matches.length === 0;
      button.setAttribute('aria-label', `${button.dataset.atlasPlace}: ${matches.length} ${matches.length === 1 ? 'release' : 'releases'}`);
      const regionCount = button.querySelector('.rf-library__region-count');
      if (regionCount) regionCount.textContent = `${matches.length} ${matches.length === 1 ? 'release' : 'releases'}`;
      const indexCount = button.querySelector('.rf-library__index-count');
      if (indexCount) indexCount.textContent = String(matches.length);
    }
  }

  #announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.requestAnimationFrame(() => { this.liveRegion.textContent = message; });
  }
}

if (!customElements.get('rf-library-explorer')) {
  customElements.define('rf-library-explorer', RfLibraryExplorer);
}
